<script lang="ts">
  // Components
  import Additem from '$icons/AddItemIcon.svelte';
  import TickCircle from '$icons/TickCircleIcon.svelte';
  // Svelte imports
  import { fly } from 'svelte/transition';
  import { createEventDispatcher, onMount } from 'svelte';
  // Types
  import type { IDetails, PostMedia } from 'garden-common';
  // Utils
  import { getResized } from 'garden-utils/index';
  import { VideoExts } from 'garden-common/index';
  import Media from '../media/Media.svelte';
  import ImageProgressBar from './ImageProgressBar.svelte';

  export let media: PostMedia<IDetails<string>>[] = [];
  export let mediaIndex: number = 0;
  export let groupIndex: number = 0;
  export let hideButton: boolean = false;
  export let mode: 'normal' | 'group' = 'normal';

  export let selected: [number, number] = [0, 0];
  let groupSelected: [number, number][] = [];

  $: if (mode === 'normal') groupSelected = [];

  const getName = (image: string | File) => {
    if (!image) {
      return 'No Media';
    } else if (typeof image === 'string') {
      if (image.split(':').length) {
        return image.split(':')[image.split(':').length - 1];
      }
      return image;
    } else {
      return image.name;
    }
  };
  const dispatch = createEventDispatcher();

  const getResizedVersion = async (ImageSource: string | File) => {
    let valuePromise: Promise<string>;
    if (ImageSource instanceof File) {
      valuePromise = new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = event => {
          const imageSrc = event.target?.result as string;
          resolve(imageSrc);
        };
        reader.onerror = error => reject(error);
        reader.readAsDataURL(ImageSource);
      });
    } else if (typeof ImageSource === 'string') {
      // Instead of trying to get the image resized just pass
      // prop as is to use the thumbnail version since
      // the backend should have resized it already
      return ImageSource;
    } else {
      // if neither ( shouldnt happen )
      throw new Error('Invalid ImageSource type');
    }
    let loadedImage = await valuePromise;
    if (loadedImage) {
      // Resize it to 400 by 400 , the same as what the backend would do
      try {
        if (
          typeof loadedImage === 'string' &&
          VideoExts.includes(loadedImage?.split('.')[4] as any)
        )
          return loadedImage;
        let resizedImage = await getResized(loadedImage, 400, 400);
        return resizedImage;
      } catch (error) {
        return ImageSource;
      }
    } else {
      // resizing failed, use full res
      return ImageSource;
    }
  };

  $: {
    if (selected[0] != mediaIndex || selected[1] != groupIndex) {
      selected = [mediaIndex, groupIndex];
    }
  }
  onMount(() => {
    if (selected[0] != mediaIndex || selected[1] != groupIndex) {
      selected = [mediaIndex, groupIndex];
    }
  });
</script>

<div class="flex flex-col w-[96px] isolate ">
  <div class="flex flex-col">
    {#each media as group, idxg}
      <div class:group={group.thumbnails?.length > 1} class:create-mode={mode === 'group'}>
        {#each group.thumbnails as src, idx}
          {@const imgkey = getName(src)}
          <!-- svelte-ignore a11y-no-static-element-interactions -->
          <div
            transition:fly|global={{ x: 50, duration: 200 }}
            class="image-item"
            class:border-b-0={idx === 0}
            class:selected={idxg === selected[0] && idx === selected[1]}
            id="image-item-{idx}-{idxg}"
            on:keydown
            on:click={() => {
              if (mode === 'group' && group.thumbnails.length === 1) {
                if (groupSelected.find(e => e[0] === idxg && e[1] === idx)) {
                  groupSelected = groupSelected.filter(e => !(e[0] === idxg && e[1] === idx));
                } else {
                  groupSelected.push([idxg, idx]);
                  groupSelected = groupSelected;
                }
                dispatch('select', groupSelected);
              } else {
                if (idxg === selected[0] && idx === selected[1]) return;
                mediaIndex = idxg;
                groupIndex = idx;
                selected = [idxg, idx];
                dispatch('select', selected);
              }
            }}
          >
            <small class="absolute top-2 left-2 w-20 z-50">
              <ImageProgressBar image={imgkey} gi={idxg} />
            </small>
            <div
              class="w-[96px] h-full absolute bg-white/40 hover:bg-white/0 ease-out duration-200 rounded-xl"
            />
            {#if group.thumbnails.length === 1}
              {#if groupSelected.find(e => e[0] === idxg && e[1] === idx)}
                <div
                  class="w-6 h-6 rounded-full bg-[#a2a2a2] border-2 border-white absolute bottom-1 right-1 cursor-pointer flex items-center justify-center"
                  class:hidden={!(mode === 'group')}
                >
                  <TickCircle class="stroke-white" />
                </div>
              {:else}
                <div
                  class="w-6 h-6 rounded-full bg-[#FFFFFF99] border border-[#808080] absolute bottom-1 right-1 cursor-pointer flex items-center justify-center"
                  class:hidden={!(mode === 'group')}
                />
              {/if}
            {/if}
            {#if typeof src === 'string'}
              <Media class="w-full h-full object-cover rounded-10" {src} autoplay={false} />
            {:else}
              <Media
                class="w-full h-full object-cover rounded-10"
                src={URL.createObjectURL(src)}
                autoplay={false}
              />
            {/if}
          </div>
        {/each}
      </div>
    {/each}
  </div>

  {#if !hideButton}
    {#if mode === 'normal'}
      <!-- svelte-ignore a11y-no-static-element-interactions -->
      <div
        class="h-[56px] border-2 border-garden-grey-300 mt-8 bg-white text-garden-grey-700 text-sm font-medium flex flex-row justify-center items-center gap-2 rounded-xl py-4 px-5 select-none cursor-pointer"
        on:keydown
        on:click={() => dispatch('additem')}
      >
        <Additem /> Add
      </div>
    {:else}
      <!-- svelte-ignore a11y-no-static-element-interactions -->
      <div
        class="h-[56px] border-2 border-garden-grey-300 mt-8 bg-white text-grey-neutral-50 cursor-not-allowed text-sm font-medium flex flex-row justify-center items-center gap-2 rounded-xl py-4 px-5 select-none ease-out duration-200"
        class:create-group-button-selected={groupSelected.length > 1}
        on:keydown
        on:click={() => {
          if (groupSelected.length > 1) {
            dispatch('group-create', { location: groupSelected });
            groupSelected = [];
            mode = 'normal';
          }
        }}
      >
        Create
      </div>
    {/if}
  {/if}
</div>

<style type="postcss">
  .image-item {
    @apply relative w-[96px] h-[76px] -mb-4 bg-white border border-white p-[2px] rounded-xl ease-out transition-all duration-200 cursor-pointer;
  }
  .image-item:hover {
    @apply h-[123px];
  }
  .selected {
    @apply outline-[#479b5f] outline outline-2 h-[123px];
  }

  .group {
    @apply relative w-full h-full outline-2 outline-dashed outline-accent-blue-60 rounded-xl pb-4 -mb-4 flex flex-col;
  }

  .create-mode {
    @apply mb-8 gap-6 ease-out transition-all duration-200;
  }

  .create-group-button-selected {
    @apply text-[#479b5f] cursor-pointer;
  }
</style>
